import { removeTrapFocus, trapFocus } from "@theme/focus";
import { morph, MORPH_OPTIONS } from "@theme/morph";

class FiltersResultsList extends HTMLElement {
  #triggerElement = null;

  constructor() {
    super();
    this.desktopMatchMedia = window.matchMedia("(min-width: 64em)");
  }

  connectedCallback() {
    this.elements = {
      filtersToggles: this.querySelectorAll("[data-filters-toggle]"),
      gridLayoutToggle: this.querySelectorAll("[data-grid-layout-toggle]"),
      filterFacetsContainer: this.querySelector(
        "[data-filter-facets-container]",
      ),
    };

    if (this.elements.filtersToggles.length) {
      this.elements.filtersToggles.forEach((toggle) => {
        toggle.addEventListener(
          "click",
          this.toggleFiltersVisibility.bind(this),
        );
      });
    }

    if (this.elements.gridLayoutToggle.length) {
      this.elements.gridLayoutToggle.forEach((toggle) => {
        toggle.addEventListener(
          "change",
          this.handleGridLayoutChange.bind(this),
        );
      });
    }

    if (window.productCompareHandle) {
      window.productCompareHandle();
    }
  }

  toggleFiltersVisibility(event) {
    const currentDesktopState = this.dataset.filterDisplay;
    const currentMobileState = this.dataset.mobileFilterDisplay;

    const newMobileState = currentMobileState === "open" ? "closed" : "open";
    this.dataset.mobileFilterDisplay = newMobileState;
    const newDesktopState = currentDesktopState === "open" ? "closed" : "open";
    this.dataset.filterDisplay = newDesktopState;

    /**
     * - If we're on mobile and the filters are now open, trap the focus
     * - Otherwise, clear the focus trap.
     */
    if (this.desktopMatchMedia.matches) {
      if (newDesktopState === "open") {
        this.elements.filterFacetsContainer.removeAttribute("inert");
      } else {
        this.elements.filterFacetsContainer.setAttribute("inert", "true");
      }
    } else {
      if (newMobileState === "open") {
        trapFocus(this.elements.filterFacetsContainer);
        this.#triggerElement = event.currentTarget;
        this.#setMediaListener();
      } else {
        removeTrapFocus(this.#triggerElement);
        this.#triggerElement = null;
      }
    }
  }

  handleGridLayoutChange(event) {
    const selectedLayout = event.target.value;
    this.dataset.gridLayout = selectedLayout;
  }

  /**
   * Listen for desktop breakpoint changes to remove the focus trap if the user resizes while the filters are open on mobile.
   */
  #setMediaListener() {
    this.desktopMatchMedia.addEventListener(
      "change",
      this.#handleMediaChange.bind(this),
      { once: true },
    );
  }

  #handleMediaChange(event) {
    if (event.matches) {
      removeTrapFocus(this.#triggerElement);
      this.#triggerElement = null;
    }
  }
}

customElements.define("filters-results-list", FiltersResultsList);

class FacetFilters extends HTMLElement {
  /**
   * Set an abort controller to cancel in-flight search requests.
   * @type {AbortController|null}
   */
  #activeFetchController = null;

  constructor() {
    super();
    this.handleChange = this.#handleChange.bind(this);
  }

  connectedCallback() {
    this.elements = {
      form: this.querySelector("form"),
      sortByRadios: this.querySelectorAll("input[name='sort_by']"),
    };

    if (!this.elements.form) {
      console.warn(
        "FacetFilters: No form element found within facet-filters component.",
      );
      return;
    }

    this.addEventListener("change", this.handleChange);
  }

  #createAbortController() {
    const abortController = new AbortController();

    // Abort any in-flight request before creating a new one
    if (this.#activeFetchController) {
      this.#activeFetchController.abort();
    }

    this.#activeFetchController = abortController;
    return abortController;
  }

  async #handleChange(event) {
    // Handle change events if necessary
    await this.#submitFilters();
  }

  async #submitFilters() {
    const currentParams = new URLSearchParams(window.location.search);
    const formData = new FormData(this.elements.form);
    const searchParams = new URLSearchParams(formData);

    const paramsToPreserve = ["q", "view"];
    paramsToPreserve.forEach((param) => {
      if (currentParams.has(param) && !searchParams.has(param)) {
        searchParams.set(param, currentParams.get(param));
      }
    });

    // Clear price parameters if they are empty.
    if (searchParams.get("filter.v.price.gte") === "") {
      searchParams.delete("filter.v.price.gte");
    }
    if (searchParams.get("filter.v.price.lte") === "") {
      searchParams.delete("filter.v.price.lte");
    }

    const newUrl = `${window.location.pathname}?${searchParams.toString()}`;

    this.#fetchAndUpdateDOM(newUrl);
  }

  async #fetchAndUpdateDOM(url) {
    const urlForHistory = url;
    const sectionId = this.dataset.sectionId;
    const urlWithSection = new URL(url, window.location.origin);
    urlWithSection.searchParams.set("section_id", sectionId);

    try {
      const abortController = this.#createAbortController();
      const response = await fetch(urlWithSection.toString(), {
        signal: abortController.signal,
      });
      const html = await response.text();

      const destination = document.getElementById(`MainCollection`);
      if (!destination) {
        console.error(
          "FacetFilters: No element with ID 'MainCollection' found in the DOM.",
        );
        return;
      }

      const parser = new DOMParser();
      const doc = parser.parseFromString(html, "text/html");
      const newContent = doc.getElementById(`MainCollection`);

      if (!newContent) {
        console.error(
          "FacetFilters: No element with ID 'MainCollection' found in the fetched HTML.",
        );
        return;
      }

      morph(destination, newContent, {
        ...MORPH_OPTIONS,
      });

      window.history.replaceState(null, "", urlForHistory);

      if (window.productCompareHandle) {
        window.productCompareHandle();
      }
    } catch (error) {
      if (error.name === "AbortError") {
        console.log("FacetFilters: Fetch request aborted.");
        return;
      }
      console.error("Error fetching filtered results:", error);
    }
  }

  updateFilters() {
    this.#submitFilters();
  }

  updateFiltersByURL(url) {
    this.#fetchAndUpdateDOM(url);
  }

  /**
   * Update the sort radio input values to match the passed `selectedSort` value.
   * - Allows sort by elements outside of this component to update the sort by value in the form so that it gets
   *   submitted correctly when changed.
   * @param {String} selectedSort
   */
  updateSortValue(selectedSort) {
    if (this.elements.sortByRadios.length) {
      this.elements.sortByRadios.forEach((radio) => {
        radio.checked = radio.value === selectedSort;
      });
    }
  }

  clearAllFilters() {
    const inputElements = this.elements.form.querySelectorAll("input, select");
    inputElements.forEach((element) => {
      /**
       * Handle range sliders separately.
       */
      if (element.closest("price-range")) {
        return;
      }

      if (element.type === "checkbox" || element.type === "radio") {
        element.checked = false;
      } else {
        element.value = "";
      }
    });

    this.#resetPriceRanges();
  }

  #resetPriceRanges() {
    const priceRangeElements =
      this.elements.form.querySelectorAll("price-range");
    priceRangeElements.forEach((priceRange) => {
      priceRange.reset();
    });
  }
}

customElements.define("facet-filters", FacetFilters);

class FilterSorting extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.elements = {
      sortSelect: this.querySelector("[data-sort-select]"),
    };

    if (this.elements.sortSelect) {
      this.elements.sortSelect.addEventListener(
        "change",
        this.handleSortChange.bind(this),
      );
    }
  }

  handleSortChange(event) {
    const selectedSort = event.target.value;
    this.updateFiltersState(selectedSort);
  }

  /**
   * Update the filter state
   * - If this component is inside a facet-filters component, just trigger an update on that component to re-render the form with the new sort option.
   * - If this component is outside of a facet-filters component, find the nearest facet-filters component in the DOM and trigger an update on that component.
   */
  updateFiltersState(selectedSort) {
    const parentFacetFilters = this.closest("facet-filters");
    if (parentFacetFilters) {
      parentFacetFilters.updateFilters();
      return;
    }

    const facetsForm =
      this.closest(".shopify-section")?.querySelector("facet-filters");

    if (!facetsForm) {
      console.warn(
        "FilterSorting: No facet-filters component found in the DOM to update.",
      );
      return;
    }

    facetsForm.updateSortValue(selectedSort);
    facetsForm.updateFilters();
  }
}

customElements.define("filter-sorting", FilterSorting);

class FilterClearAll extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.elements = {
      clearButton: this.querySelector("[data-clear-filters]"),
    };

    if (this.elements.clearButton) {
      this.elements.clearButton.addEventListener(
        "click",
        this.handleClearFilters.bind(this),
      );
    }
  }

  handleClearFilters(event) {
    event.preventDefault();
    const parentFacetFilters = this.closest("facet-filters");
    if (parentFacetFilters) {
      parentFacetFilters.clearAllFilters();
      parentFacetFilters.updateFilters();
      return;
    }

    const facetFilters =
      this.closest(".shopify-section")?.querySelector("facet-filters");

    if (!facetFilters) {
      console.warn(
        "FilterClearAll: No facet-filters component found in the DOM to clear filters on.",
      );
      return;
    }

    facetFilters.clearAllFilters();
    facetFilters.updateFilters();
  }
}

customElements.define("filter-clear-all", FilterClearAll);

class FilterRemove extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.elements = {
      removeButton: this.querySelector("[data-remove-filter]"),
    };

    if (this.elements.removeButton) {
      this.elements.removeButton.addEventListener(
        "click",
        this.handleRemoveFilter.bind(this),
      );
    }
  }

  handleRemoveFilter(event) {
    event.preventDefault();
    const button = event.currentTarget;
    const urlToRemove = button.dataset.url;

    if (!urlToRemove) {
      return;
    }

    const filterFacets =
      this.closest("facet-filters") ||
      this.closest(".shopify-section")?.querySelector("facet-filters");

    if (!filterFacets) {
      console.warn(
        "FilterRemove: No facet-filters component found in the DOM to update after removing filter.",
      );
      return;
    }

    filterFacets.updateFiltersByURL(urlToRemove);
  }
}

customElements.define("filter-remove", FilterRemove);

class SearchForm extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.elements = {
      searchForm: this.querySelector("[data-search-form]"),
    };

    if (this.elements.searchForm) {
      this.elements.searchForm.addEventListener(
        "submit",
        this.#handleSearchSubmit.bind(this),
      );
    }
  }

  #handleSearchSubmit(event) {
    // Get the search term from the form using the name attribute of the input field (e.g. q)
    const searchInput = this.elements.searchForm?.q;
    const searchTerm = searchInput?.value.trim();

    if (!searchTerm) {
      return;
    }

    const hasRedirect = this.#checkForCustomRedirect(searchTerm);
    if (hasRedirect) {
      event.preventDefault();
    }
  }

  #checkForCustomRedirect(searchTerm) {
    /**
     * @type {Array<{searchTerm: Array<string>, url: string}>}
     */
    const redirectData = window.searchCustomRedirects || [];
    if (!redirectData.length) return;

    const lowerCaseSearchTerm = searchTerm.toLowerCase();
    const redirect = redirectData.find((entry) => {
      return entry.searchTerm.some(
        (term) => term.toLowerCase() === lowerCaseSearchTerm,
      );
    });

    if (redirect) {
      window.location.href = redirect.url;
      return true;
    }
  }
}

customElements.define("search-form", SearchForm);

class PriceRange extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.elements = {
      rangeMin: this.querySelector("[data-price-range-min]"),
      rangeMax: this.querySelector("[data-price-range-max]"),
      inputMin: this.querySelector("[data-price-min]"),
      inputMax: this.querySelector("[data-price-max]"),
      labelMin: this.querySelector("[data-price-range-label-min]"),
      labelMax: this.querySelector("[data-price-range-label-max]"),
    };

    this.moneyFormat = this.dataset.moneyFormat || "{{ amount }}";
    this.rangeMaxValue = null;
    if (this.dataset.rangeMax) {
      const rangeMaxValueNumber = Number(this.dataset.rangeMax);
      if (!isNaN(rangeMaxValueNumber)) {
        this.rangeMaxValue = rangeMaxValueNumber;
      }
    }

    this.ranges = [this.elements.rangeMin, this.elements.rangeMax];
    this.ranges.forEach((range) => {
      range.addEventListener("input", this.#handleRangeInput.bind(this));
    });
  }

  #handleRangeInput() {
    this.#updateUI();
  }

  #updateUI() {
    const { minValue, maxValue } = this.#computeMinAndMax();

    // Set hidden inputs
    this.elements.inputMin.value = minValue === 0 ? "" : minValue;
    this.elements.inputMax.value =
      maxValue === this.rangeMaxValue ? "" : maxValue;

    // Set styles
    this.style.setProperty("--range-current-min", minValue);
    this.style.setProperty("--range-current-max", maxValue);

    // Update displayed values
    this.updateLabels(minValue, maxValue);
  }

  /**
   * Compute the minimum and maximum values from the range inputs.
   * - Handles cases where the min value is greater than the max value by swapping them to ensure the correct values are always set.
   * @returns {{ minValue: number, maxValue: number }} computed min and max values
   */
  #computeMinAndMax() {
    const rangeMinValue = Number(this.elements.rangeMin.value);
    const rangeMaxValue = Number(this.elements.rangeMax.value);
    const minValue = Math.min(rangeMinValue, rangeMaxValue);
    const maxValue = Math.max(rangeMinValue, rangeMaxValue);
    return { minValue, maxValue };
  }

  updateLabels() {
    const { minValue, maxValue } = this.#computeMinAndMax();

    // Update displayed values
    if (this.elements.labelMin) {
      const formattedMin = Shopify.formatMoney(
        minValue * 100,
        this.moneyFormat,
      );
      this.elements.labelMin.textContent = formattedMin;
    }

    if (this.elements.labelMax) {
      const formattedMax = Shopify.formatMoney(
        maxValue * 100,
        this.moneyFormat,
      );
      this.elements.labelMax.textContent = formattedMax;
    }
  }

  reset() {
    const resetMaxValue = this.rangeMaxValue || 100;
    this.style.setProperty("--range-current-min", 0);
    this.style.setProperty("--range-current-max", resetMaxValue);
    if (this.elements.rangeMin) this.elements.rangeMin.value = 0;
    if (this.elements.rangeMax) this.elements.rangeMax.value = resetMaxValue;
    if (this.elements.inputMin) this.elements.inputMin.value = "";
    if (this.elements.inputMax) this.elements.inputMax.value = "";
    this.updateLabels(0, resetMaxValue);
  }
}

customElements.define("price-range", PriceRange);
